import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Building,
  Image as ImageIcon,
  Loader2,
  MapPin,
  Phone,
  Plus,
  Save,
  Search,
  Star,
  Trash2,
  Users,
  X,
} from "lucide-react";
import { toast } from "sonner";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useCreateUnit, useUnitDetail, useUpdateUnit } from "@/hooks/useUnits";
import { useUsers } from "@/hooks/useUsers";
import { useTenantDetail } from "@/hooks/useTenants";
import { getTenantId } from "@/lib/apiClient";
import {
  BRAZILIAN_STATES,
  fetchViaCep,
} from "@/services/viacep";

import {
  maskBRPhone,
  maskCEP,
  maskCNPJ,
  normalizeCepToApi,
} from "@/lib/masks";
import { useAuth } from "@/contexts/AuthContext";
import { PERMISSIONS } from "@/lib/permissions";
import { PermissionButton } from "@/components/shared/PermissionButton";

type ContactKind = "email" | "phone" | "whatsapp" | "other";
type ContactForm = { kind: ContactKind; value: string; is_primary: boolean };

type AddressForm = {
  label: string;
  zip: string;
  street: string;
  number: string;
  complement: string;
  district: string;
  city: string;
  state: string;
  country: string;
  is_primary: boolean;
};

type DeleteTarget =
  | { type: "contact"; index: number }
  | { type: "address"; index: number }
  | null;

const CONTACT_KIND_LABELS: Record<ContactKind, string> = {
  email: "E-mail",
  whatsapp: "WhatsApp",
  phone: "Telefone",
  other: "Outro",
};

const emptyAddress = (): AddressForm => ({
  label: "Principal",
  zip: "",
  street: "",
  number: "",
  complement: "",
  district: "",
  city: "",
  state: "",
  country: "BR",
  is_primary: true,
});

function formatContactValue(kind: ContactKind, value: string) {
  if (kind === "phone" || kind === "whatsapp") return maskBRPhone(value);
  return value;
}

function dataUrlToPhotoPayload(dataUrl: string | null) {
  if (!dataUrl?.startsWith("data:")) return null;

  const [meta, base64] = dataUrl.split(",");
  if (!meta || !base64) return null;

  const mimeMatch = meta.match(/^data:(.*?);base64$/);
  const mime = mimeMatch?.[1] || null;
  if (!mime) return null;

  return {
    photo_base64: base64,
    photo_mime_type: mime,
    photo_file_name: "unit-logo",
  };
}

export default function UnitFormPage() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  const { user } = useAuth();

  const isCreating = !id;
  const isEditing = !!id && location.pathname.endsWith("/editar");
  const isViewing = !!id && !location.pathname.endsWith("/editar");
  const readOnly = isViewing;
  const canWriteUnits = user?.permissions?.includes(PERMISSIONS.UNITS_WRITE) ?? false;

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [saving, setSaving] = useState(false);

  const currentTenantId = getTenantId() || undefined;

  const detailQuery = useUnitDetail(id);
  const tenantQuery = useTenantDetail(currentTenantId);
  const createMut = useCreateUnit();
  const updateMut = useUpdateUnit();

  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [userSearch, setUserSearch] = useState("");
  const [assignedUserIds, setAssignedUserIds] = useState<string[]>([]);

  const usersQuery = useUsers({
    q: userSearch,
    limit: 100,
    offset: 0,
  });

  const [form, setForm] = useState({
    name: "",
    legal_name: "",
    cnpj: "",
    is_active: true,
  });

  const [contacts, setContacts] = useState<ContactForm[]>([
    { kind: "email", value: "", is_primary: true },
  ]);

  const [addresses, setAddresses] = useState<AddressForm[]>([emptyAddress()]);

  useEffect(() => {
    if (!detailQuery.data) return;

    const { unit, person, contacts: apiContacts, addresses: apiAddresses, users } = detailQuery.data;

    setForm({
      name: person.name || "",
      legal_name: person.legal_name || "",
      cnpj: person.document_number ? maskCNPJ(person.document_number) : "",
      is_active: unit.is_active ?? true,
    });

    setContacts(
      apiContacts.length
        ? apiContacts.map((c) => ({
            kind: c.kind,
            value:
              c.kind === "phone" || c.kind === "whatsapp"
                ? maskBRPhone(c.value)
                : c.value,
            is_primary: c.is_primary,
          }))
        : [{ kind: "email", value: "", is_primary: true }],
    );

    setAddresses(
      apiAddresses.length
        ? apiAddresses.map((a, index) => ({
            label: a.label || "Principal",
            zip: a.zip ? maskCEP(a.zip) : "",
            street: a.street || "",
            number: a.number || "",
            complement: a.complement || "",
            district: a.district || "",
            city: a.city || "",
            state: a.state || "",
            country: a.country || "BR",
            is_primary: a.is_primary || index === 0,
          }))
        : [emptyAddress()],
    );

    setAssignedUserIds((users || []).map((u: any) => u.id));

    if (person.photo?.base64 && person.photo?.mime_type) {
      setLogoPreview(`data:${person.photo.mime_type};base64,${person.photo.base64}`);
    } else {
      setLogoPreview(null);
    }
  }, [detailQuery.data]);

  const busy =
    detailQuery.isLoading ||
    tenantQuery.isLoading ||
    createMut.isPending ||
    updateMut.isPending;

  const primaryAddressCount = useMemo(
    () => addresses.filter((a) => a.is_primary).length,
    [addresses],
  );

  const availableUsers = useMemo(() => {
    return (usersQuery.data ?? []).filter((u: any) => u.is_active);
  }, [usersQuery.data]);

  const companyName = isEditing || isViewing
    ? detailQuery.data?.tenant?.tenant_name || tenantQuery.data?.person?.name || ""
    : tenantQuery.data?.person?.name || "";

  const companyDocument = isEditing || isViewing
    ? detailQuery.data?.tenant?.tenant_document_number || tenantQuery.data?.person?.document_number || ""
    : tenantQuery.data?.person?.document_number || "";

  function goBack() {
    navigate("/cadastros/unidades");
  }

  function handleLogoUpload(e: React.ChangeEvent<HTMLInputElement>) {
    if (readOnly) return;

    const file = e.target.files?.[0];
    if (!file) return;

    if (!["image/jpeg", "image/png"].includes(file.type)) {
      toast.error("Use JPG ou PNG.");
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast.error("Máximo 2MB.");
      return;
    }

    const reader = new FileReader();
    reader.onload = () => setLogoPreview(reader.result as string);
    reader.readAsDataURL(file);
  }

  function removeLogo() {
    if (readOnly) return;
    setLogoPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  function addContact() {
    if (readOnly) return;
    setContacts((prev) => [...prev, { kind: "whatsapp", value: "", is_primary: false }]);
  }

  function removeContact(idx: number) {
    if (readOnly) return;
    setContacts((prev) => {
      const next = [...prev];
      next.splice(idx, 1);
      if (next.length > 0 && !next.some((c) => c.is_primary)) next[0].is_primary = true;
      return next.length ? next : [{ kind: "email", value: "", is_primary: true }];
    });
  }

  function setPrimaryContact(idx: number) {
    if (readOnly) return;
    setContacts((prev) => prev.map((c, i) => ({ ...c, is_primary: i === idx })));
  }

  function updateContact(idx: number, field: keyof ContactForm, value: any) {
    if (readOnly) return;
    setContacts((prev) => {
      const next = [...prev];
      if (field === "kind") {
        next[idx] = {
          ...next[idx],
          kind: value as ContactKind,
          value: formatContactValue(value as ContactKind, next[idx].value),
        };
      } else {
        next[idx] = {
          ...next[idx],
          [field]: field === "value" ? formatContactValue(next[idx].kind, value) : value,
        };
      }
      return next;
    });
  }

  function addAddress() {
    if (readOnly) return;
    setAddresses((prev) => [...prev, { ...emptyAddress(), is_primary: false }]);
  }

  function removeAddress(idx: number) {
    if (readOnly) return;
    setAddresses((prev) => {
      const next = [...prev];
      next.splice(idx, 1);
      if (next.length > 0 && !next.some((a) => a.is_primary)) next[0].is_primary = true;
      return next.length ? next : [emptyAddress()];
    });
  }

  function setPrimaryAddress(idx: number) {
    if (readOnly) return;
    setAddresses((prev) => prev.map((a, i) => ({ ...a, is_primary: i === idx })));
  }

  function setAddressField(idx: number, key: keyof AddressForm, value: string | boolean) {
    if (readOnly) return;
    setAddresses((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [key]: value } as AddressForm;
      return next;
    });
  }

  async function handleCepBlur(addrIdx: number) {
    if (readOnly) return;

    const cep = normalizeCepToApi(addresses[addrIdx]?.zip || "");
    if (!cep || cep.length !== 8) return;

    const data = await fetchViaCep(cep);
    if (!data) return;

    setAddresses((prev) => {
      const next = [...prev];
      const cur = next[addrIdx] || emptyAddress();

      next[addrIdx] = {
        ...cur,
        zip: maskCEP(data.cep || cur.zip),
        street: data.logradouro || cur.street,
        complement: data.complemento || cur.complement,
        district: data.bairro || cur.district,
        city: data.localidade || cur.city,
        state: (data.uf || cur.state).toUpperCase(),
        country: "BR",
      };

      return next;
    });
  }

  function confirmDeleteTarget() {
    if (!deleteTarget || readOnly) return;

    if (deleteTarget.type === "contact") {
      removeContact(deleteTarget.index);
      toast.success("Contato removido");
    } else {
      removeAddress(deleteTarget.index);
      toast.success("Endereço removido");
    }

    setDeleteTarget(null);
  }

  async function handleSave() {
    if (readOnly) return;

    if (!form.name.trim()) {
      toast.error("Informe o nome da unidade");
      return;
    }

    if (!form.legal_name.trim()) {
      toast.error("Informe a razão social");
      return;
    }

    if (!form.cnpj.trim()) {
      toast.error("Informe o CNPJ");
      return;
    }

    const validAddresses = addresses.filter((a) =>
      [a.zip, a.street, a.number, a.district, a.city, a.state].some((v) => (v || "").trim()),
    );

    if (validAddresses.length === 0) {
      toast.error("Informe ao menos um endereço");
      return;
    }

    if (primaryAddressCount === 0) {
      toast.error("Defina um endereço principal");
      return;
    }

    const body = {
      person: {
        person_type: "PJ" as const,
        name: form.name.trim(),
        legal_name: form.legal_name.trim(),
        document_number: form.cnpj || null,
        photo: dataUrlToPhotoPayload(logoPreview),
        contacts: contacts
          .filter((c) => c.value?.trim())
          .map((c) => ({
            kind: c.kind,
            value: c.value.trim(),
            is_primary: c.is_primary,
          })),
        addresses: addresses
          .filter((a) =>
            [a.zip, a.street, a.number, a.district, a.city, a.state].some((v) => (v || "").trim()),
          )
          .map((a) => ({
            label: a.label || null,
            zip: a.zip || null,
            street: a.street || null,
            number: a.number || null,
            complement: a.complement || null,
            district: a.district || null,
            city: a.city || null,
            state: a.state || null,
            country: a.country || "BR",
            is_primary: a.is_primary,
          })),
      },
      assigned_user_ids: assignedUserIds,
      is_active: form.is_active,
    };

    setSaving(true);
    try {
      if (isEditing) {
        await updateMut.mutateAsync({ id: id!, body });
        toast.success("Unidade atualizada com sucesso");
      } else if (isCreating) {
        await createMut.mutateAsync(body);
        toast.success("Unidade cadastrada com sucesso");
      }

      navigate("/cadastros/unidades");
    } catch (e: any) {
      toast.error(e?.message || "Erro ao salvar unidade");
    } finally {
      setSaving(false);
    }
  }

  if ((isEditing || isViewing) && detailQuery.isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="pb-10">
      <div className="sticky top-14 z-20 -mx-1 mb-6 border-b bg-background/95 px-1 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="flex flex-col gap-3 py-4 md:flex-row md:items-center md:justify-between">
          <div className="flex items-start gap-3">
            <Button variant="outline" size="icon" onClick={goBack} disabled={busy}>
              <ArrowLeft className="h-4 w-4" />
            </Button>

            <div>
              <h1 className="text-xl font-bold tracking-tight">
                {isViewing ? "Visualizar Unidade" : isEditing ? "Editar Unidade" : "Nova Unidade"}
              </h1>
              <p className="text-sm text-muted-foreground">
                {isViewing
                  ? "Consulta dos dados da unidade"
                  : "Cadastro de unidade com empresa vinculada, contatos, endereço e funcionários"}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 self-end md:self-auto">
            <Button variant="outline" onClick={goBack} disabled={saving}>
              {isViewing ? "Voltar" : "Cancelar"}
            </Button>

            {isViewing ? (
              <PermissionButton
                permission={PERMISSIONS.UNITS_WRITE}
                onClick={() => navigate(`/cadastros/unidades/${id}/editar`)}
              >
                Editar
              </PermissionButton>
            ) : (
              <Button onClick={handleSave} disabled={saving}>
                {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                Salvar
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Building className="h-4 w-4 text-primary" />
              Dados da Unidade
            </CardTitle>
          </CardHeader>

          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-[220px_1fr]">
              <div>
                <Label className="mb-2 block">Logo</Label>

                <div className="rounded-xl border border-dashed p-3">
                  <div className="flex aspect-square items-center justify-center overflow-hidden rounded-lg border bg-muted/30">
                    {logoPreview ? (
                      <img src={logoPreview} alt="Logo da unidade" className="h-full w-full object-contain" />
                    ) : (
                      <div className="flex flex-col items-center gap-2 text-muted-foreground">
                        <ImageIcon className="h-8 w-8" />
                        <span className="px-2 text-center text-xs">JPG ou PNG até 2MB</span>
                      </div>
                    )}
                  </div>

                  {!readOnly ? (
                    <div className="mt-3 flex gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        className="flex-1"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <ImageIcon className="mr-2 h-4 w-4" />
                        {logoPreview ? "Trocar" : "Enviar"}
                      </Button>

                      {logoPreview ? (
                        <Button type="button" variant="ghost" size="icon" onClick={removeLogo}>
                          <X className="h-4 w-4" />
                        </Button>
                      ) : null}
                    </div>
                  ) : null}

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png"
                    className="hidden"
                    onChange={handleLogoUpload}
                    disabled={readOnly}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="md:col-span-2">
                  <Label>Empresa</Label>
                  <Input value={companyName} disabled className="mt-1" />
                </div>

                <div>
                  <Label>CNPJ Empresa</Label>
                  <Input value={companyDocument ? maskCNPJ(companyDocument) : ""} disabled className="mt-1" />
                </div>

                <div className="md:col-span-2">
                  <Label>Nome da Unidade *</Label>
                  <Input
                    value={form.name}
                    onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                    className="mt-1"
                    disabled={readOnly}
                  />
                </div>

                <div>
                  <Label>Razão Social *</Label>
                  <Input
                    value={form.legal_name}
                    onChange={(e) => setForm((prev) => ({ ...prev, legal_name: e.target.value }))}
                    className="mt-1"
                    disabled={readOnly}
                  />
                </div>

                <div>
                  <Label>CNPJ Unidade *</Label>
                  <Input
                    value={form.cnpj}
                    onChange={(e) => setForm((prev) => ({ ...prev, cnpj: maskCNPJ(e.target.value) }))}
                    className="mt-1"
                    placeholder="00.000.000/0001-00"
                    disabled={readOnly}
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-base">
              <Users className="h-4 w-4 text-primary" />
              Funcionários da unidade
            </CardTitle>
          </CardHeader>

          <CardContent className="space-y-4">
            <div className="max-w-md">
              <Label>Buscar usuário</Label>
              <Input
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                placeholder="Buscar por nome, e-mail ou matrícula"
                className="mt-1"
                disabled={readOnly}
              />
            </div>

            <div className="rounded-lg border">
              <div className="max-h-[320px] overflow-auto">
                <div className="divide-y">
                  {availableUsers.length === 0 ? (
                    <div className="p-4 text-sm text-muted-foreground">
                      Nenhum usuário encontrado.
                    </div>
                  ) : (
                    availableUsers.map((u: any) => {
                      const checked = assignedUserIds.includes(u.id);

                      return (
                        <label
                          key={u.id}
                          className="flex items-start gap-3 p-4 hover:bg-muted/40"
                        >
                          <Checkbox
                            checked={checked}
                            disabled={readOnly}
                            onCheckedChange={(value) => {
                              if (readOnly) return;
                              const nextChecked = !!value;
                              setAssignedUserIds((prev) =>
                                nextChecked
                                  ? [...new Set([...prev, u.id])]
                                  : prev.filter((userId) => userId !== u.id),
                              );
                            }}
                          />

                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-sm">{u.name}</div>
                            <div className="text-xs text-muted-foreground">
                              {u.email || "Sem e-mail"}
                            </div>
                          </div>
                        </label>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-base">
              <Phone className="h-4 w-4 text-primary" />
              Contatos
            </CardTitle>

            {!readOnly ? (
              <Button variant="outline" size="sm" onClick={addContact}>
                <Plus className="mr-2 h-4 w-4" />
                Adicionar
              </Button>
            ) : null}
          </CardHeader>

          <CardContent className="space-y-3">
            {contacts.map((c, idx) => (
              <div key={idx} className="rounded-lg border p-3">
                <div className="grid grid-cols-1 gap-3 md:grid-cols-[180px_1fr_auto_auto] md:items-end">
                  <div>
                    <Label>Tipo</Label>
                    <Select
                      value={c.kind}
                      onValueChange={(v) => updateContact(idx, "kind", v)}
                      disabled={readOnly}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(CONTACT_KIND_LABELS).map(([key, label]) => (
                          <SelectItem key={key} value={key}>
                            {label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Valor</Label>
                    <Input
                      value={c.value}
                      onChange={(e) => updateContact(idx, "value", e.target.value)}
                      className="mt-1"
                      disabled={readOnly}
                    />
                  </div>

                  {!readOnly ? (
                    <>
                      <Button
                        type="button"
                        variant={c.is_primary ? "default" : "outline"}
                        size="sm"
                        onClick={() => setPrimaryContact(idx)}
                      >
                        <Star className={`mr-2 h-4 w-4 ${c.is_primary ? "fill-current" : ""}`} />
                        Principal
                      </Button>

                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        disabled={contacts.length === 1}
                        onClick={() => setDeleteTarget({ type: "contact", index: idx })}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </>
                  ) : (
                    <div className="md:col-span-2" />
                  )}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-base">
              <MapPin className="h-4 w-4 text-primary" />
              Endereços
            </CardTitle>

            {!readOnly ? (
              <Button variant="outline" size="sm" onClick={addAddress}>
                <Plus className="mr-2 h-4 w-4" />
                Adicionar
              </Button>
            ) : null}
          </CardHeader>

          <CardContent className="space-y-4">
            {addresses.map((a, idx) => (
              <div key={idx} className="rounded-xl border p-4">
                <div className="mb-4 flex flex-wrap items-center gap-2">
                  <Input
                    value={a.label}
                    onChange={(e) => setAddressField(idx, "label", e.target.value)}
                    className="h-9 w-40"
                    placeholder="Rótulo"
                    disabled={readOnly}
                  />

                  {!readOnly ? (
                    <>
                      <Button
                        type="button"
                        variant={a.is_primary ? "default" : "outline"}
                        size="sm"
                        onClick={() => setPrimaryAddress(idx)}
                      >
                        <Star className={`mr-2 h-4 w-4 ${a.is_primary ? "fill-current" : ""}`} />
                        Principal
                      </Button>

                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        disabled={addresses.length === 1}
                        onClick={() => setDeleteTarget({ type: "address", index: idx })}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </>
                  ) : (
                    a.is_primary ? <Badge>Principal</Badge> : null
                  )}
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-12">
                  <div className="md:col-span-3">
                    <Label>CEP</Label>
                    <Input
                      value={a.zip}
                      onChange={(e) => setAddressField(idx, "zip", maskCEP(e.target.value))}
                      onBlur={() => handleCepBlur(idx)}
                      className="mt-1"
                      disabled={readOnly}
                    />
                  </div>

                  <div className="md:col-span-7">
                    <Label>Rua</Label>
                    <Input
                      value={a.street}
                      onChange={(e) => setAddressField(idx, "street", e.target.value)}
                      className="mt-1"
                      disabled={readOnly}
                    />
                  </div>

                  <div className="md:col-span-2">
                    <Label>Número</Label>
                    <Input
                      value={a.number}
                      onChange={(e) => setAddressField(idx, "number", e.target.value)}
                      className="mt-1"
                      disabled={readOnly}
                    />
                  </div>

                  <div className="md:col-span-4">
                    <Label>Complemento</Label>
                    <Input
                      value={a.complement}
                      onChange={(e) => setAddressField(idx, "complement", e.target.value)}
                      className="mt-1"
                      disabled={readOnly}
                    />
                  </div>

                  <div className="md:col-span-4">
                    <Label>Bairro</Label>
                    <Input
                      value={a.district}
                      onChange={(e) => setAddressField(idx, "district", e.target.value)}
                      className="mt-1"
                      disabled={readOnly}
                    />
                  </div>

                  <div className="md:col-span-3">
                    <Label>Cidade</Label>
                    <Input
                      value={a.city}
                      onChange={(e) => setAddressField(idx, "city", e.target.value)}
                      className="mt-1"
                      disabled={readOnly}
                    />
                  </div>

                  <div className="md:col-span-1">
                    <Label>UF</Label>
                    <Select
                      value={a.state || ""}
                      onValueChange={(value) => setAddressField(idx, "state", value)}
                      disabled={readOnly}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="UF" />
                      </SelectTrigger>
                      <SelectContent>
                        {BRAZILIAN_STATES.map((uf) => (
                          <SelectItem key={uf.value} value={uf.value}>
                            {uf.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="md:col-span-2">
                    <Label>País</Label>
                    <Input
                      value={a.country}
                      onChange={(e) => setAddressField(idx, "country", e.target.value.toUpperCase())}
                      className="mt-1"
                      disabled={readOnly}
                    />
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title="Remover item"
        description="Deseja remover este item do formulário?"
        onConfirm={confirmDeleteTarget}
        confirmLabel="Remover"
        variant="destructive"
      />
    </div>
  );
}
